import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import Label from "../Label/Label";
import HintLabel from "../HintLabel/HintLabel";
import Button from "../Button/Button";
import ErrorLabelWithValue from "../ErrorLabelWithValue/ErrorLabelWithValue";
import InfoIcon from "../InfoIcon/InfoIcon";
import InputDescription from "../InputDescription/InputDescription";
import PendingFieldConfirmButton from "../PendingFieldConfirmButton/PendingFieldConfirmButton";
import "./duration-select.scss";
import { isFocused } from "../../utilities";

const DurationSelect = ({
  value,
  messages,
  placeholder,
  disabled,
  onSubmit,
  isPending
}) => {
  const {
    years = "years",
    months = "months",
    days = "days",
    hours = "hours",
    minutes = "minutes",
    seconds = "seconds",
    ok = "Ok",
  } = messages;
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState(moment.duration());
  const [durationISO, setDurationISO] = useState("");
  const [options, setOptions] = useState([
    { key: "years", title: years, value: 0 },
    { key: "months", title: months, value: 0 },
    { key: "days", title: days, value: 0 },
    { key: "hours", title: hours, value: 0 },
    { key: "minutes", title: minutes, value: 0 },
    { key: "seconds", title: seconds, value: 0 },
  ]);
  const dropRef = useRef(null);
  const durationSelectRef = useRef(null);

  const handleChange = ({ target }) => {
    const { name, value } = target;
    const updatedOptions = options.map((option) => {
      if (option.key === name) {
        return { ...option, value: parseInt(value) };
      }
      return option;
    });

    setOptions(updatedOptions);
  };

  const hanldeDurationChange = ({ target }) => {
    const { value } = target;
    setDurationISO(value);
  };

  const handleSubmit = () => {
    setDuration(duration);
    setDurationISO(duration.toISOString());
    setOpen(!open);
    onSubmit(duration);
  };

  const humanizeDuration = (value) => {
    const duration = moment.duration(value);
    return options.map(({ key, title }) => {
      const unitValue = duration.get(key);
      return unitValue > 0 ? ` ${unitValue} ${title}` : "";
    });
  };

  const isValidDuration = (iso8601DurationString) => {
    if (iso8601DurationString === "P0D") return true;
    return moment.duration(iso8601DurationString).toISOString() !== "P0D";
  };

  const getDurationValueByType = (type) =>
    options.find((item) => item.key === type).value || 0;

  const handleClickOutside = (event) => {
    if (dropRef.current && !dropRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const duration = moment.duration({
      seconds: getDurationValueByType("seconds"),
      minutes: getDurationValueByType("minutes"),
      hours: getDurationValueByType("hours"),
      days: getDurationValueByType("days"),
      months: getDurationValueByType("months"),
      years: getDurationValueByType("years"),
    });

    setDuration(duration);
    setDurationISO(duration.toISOString());
  }, [options]);

  useEffect(() => {
    if (value) {
      const duration = moment.duration(value);
      const updatedOptions = options.map((option) => {
        return { ...option, value: duration.get(option.key) };
      });

      setDurationISO(value);
      setDuration(duration);
      setOptions(updatedOptions);
    }
  }, [value]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropRef]);

  useEffect(() => {
    if (!open && dropRef && !dropRef.current && durationISO && duration !== value) {
      onSubmit(durationISO);
    }
  }, [open]);

  useEffect(() => {
    setDurationISO("");
  }, []);

  return (
    <div className="dcrform-duration">
      <input
        ref={durationSelectRef}
        className="input-field__input"
        type="text"
        disabled={disabled}
        placeholder={placeholder}
        value={durationISO}
        onChange={hanldeDurationChange}
        onFocus={() => setOpen(true)}
        autoComplete="none"
      />
      {isPending && value && !(isFocused(durationSelectRef.current) || open) && (
        <PendingFieldConfirmButton onClick={() => onSubmit(value)} />
      )}
      {isValidDuration(durationISO) && (
        <em className="humanized-duration">{humanizeDuration(durationISO)}</em>
      )}
      {open && (
        <ul className="dcrform-duration__dropdown" ref={dropRef}>
          {options.map(({ title = "", key }) => (
            <li key={key}>
              <label className="dcrform-duration__title">
                {title.toUpperCase()}
              </label>
              <input
                className="dcrform-duration__value"
                type="number"
                min="0"
                name={key}
                value={duration.get(key)}
                onChange={handleChange}
              />
            </li>
          ))}
          <li>
            <Button className="dcrform-duration__btn" onClick={handleSubmit}>
              {ok}
            </Button>
          </li>
        </ul>
      )}
    </div>
  );
};

const DurationSelectInput = (props) => {
  const {
    label,
    placeholder,
    hintText,
    value,
    disabled,
    description,
    error,
    pending,
    isRequired,
    validations,
    onChange,
    onSubmit,
  } = props;
  const [show, setShow] = useState(false);
  const handleToggle = () => setShow(!show);

  return (
    <div className="input-field">
      <Label className={pending ? "pending-label" : ""}>{label}</Label>
      <div className="input-field__wrapper">
        <div className="input-field__inner-wrapper">
          <DurationSelect
            className={`input-field__input ${isRequired ? "error-border" : ""}`}
            value={value || ""}
            disabled={disabled}
            placeholder={placeholder}
            messages={{}}
            onChange={onChange}
            onSubmit={onSubmit}
            isPending={pending}
          />
          {description && <InfoIcon onClick={handleToggle} active={show} />}
        </div>
        <ErrorLabelWithValue
          isRequired={isRequired}
          error={error}
          validations={validations}
        />
        {hintText && <HintLabel>{hintText}</HintLabel>}
        {description && show && <InputDescription>{description}</InputDescription>}
      </div>
    </div>
  );
};

export default DurationSelectInput;
